import { ShipType, type ShipTypeDef, SHIP_TYPE_DEFS, SHIP_TYPE_COUNT } from '../config/ShipTypes';
import { writeShipMatrix } from './ShipMatrix';

const FLOATS_PER_MAT = 16;
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
const MAX_DISP = 64;

/** Pre-allocated formation output per ship type. */
export interface TypeSlice {
  readonly def: ShipTypeDef;
  data: Float32Array;
  count: number;
  capped: boolean;
}

/**
 * Computes world-space model matrices for all displayed ships.
 * Disc-based sunflower layout with separation enforcement.
 * Zero-allocation in compute() — all arrays pre-allocated.
 */
export class FleetFormation {
  private _spacing: number;
  private _maxRadius: number;
  private _slices: TypeSlice[] = [];
  private _total = 0;

  private _px = new Float32Array(MAX_DISP);
  private _pz = new Float32Array(MAX_DISP);
  private _py = new Float32Array(MAX_DISP);
  private _fp = new Float32Array(MAX_DISP);
  private _ti = new Uint8Array(MAX_DISP);
  private _li = new Uint8Array(MAX_DISP);

  private static readonly ORDER = [
    ShipType.ICSS, ShipType.Carrier, ShipType.Colonyship, ShipType.Receptor,
    ShipType.Battleship,
    ShipType.Bucaneer, ShipType.Fighter,
    ShipType.Scout,
  ];

  constructor(spacing: number, maxRadius: number) {
    this._spacing = spacing; this._maxRadius = maxRadius;
    for (let i = 0; i < SHIP_TYPE_COUNT; i++) {
      const def = SHIP_TYPE_DEFS[i];
      this._slices.push({ def, data: new Float32Array(def.displayMax * FLOATS_PER_MAT), count: 0, capped: false });
    }
  }

  get slices(): readonly TypeSlice[] { return this._slices; }
  get total(): number { return this._total; }
  get px(): Float32Array { return this._px; }
  get py(): Float32Array { return this._py; }
  get pz(): Float32Array { return this._pz; }
  get fp(): Float32Array { return this._fp; }

  compute(
    composition: readonly number[],
    cx: number, cy: number, cz: number,
    dirX: number, dirZ: number, turnRate: number,
    time: number, lineup = false, skipSeparation = false,
  ): void {
    if (lineup) { this._computeLineup(composition, cx, cy, cz); return; }

    let total = 0;
    for (let oi = 0; oi < FleetFormation.ORDER.length; oi++) {
      const ti = FleetFormation.ORDER[oi];
      const a = composition[ti] ?? 0, d = a === 0 ? 0 : Math.min(a, this._slices[ti].def.displayMax);
      this._slices[ti].count = d; this._slices[ti].capped = a > d; total += d;
    }
    this._total = total;
    if (total === 0) return;
    this._layoutSunflower(total, cx, cy, cz, dirX, dirZ, turnRate, time);
    if (!skipSeparation) this._separate(total, cx, cz);
    for (let gi = 0; gi < total; gi++) {
      const s = this._slices[this._ti[gi]];
      writeShipMatrix(s.data, this._li[gi], this._px[gi], this._py[gi], this._pz[gi], dirX, dirZ, s.def, turnRate);
    }
  }

  private _layoutSunflower(
    total: number, cx: number, cy: number, cz: number,
    dirX: number, dirZ: number, turnRate: number, time: number,
  ): void {
    const perpX = -dirZ, perpZ = dirX;
    const rMax = this._maxRadius * 0.85 * Math.min(1.0, Math.sqrt(total * 0.04) + 0.08);
    const wAmp = this._maxRadius * 0.025;
    let shipIdx = 0;
    for (let oi = 0; oi < FleetFormation.ORDER.length; oi++) {
      const ti = FleetFormation.ORDER[oi];
      const slice = this._slices[ti];
      if (slice.count === 0) continue;

      const damp = Math.max(0.1, 1.0 - slice.def.footprint * 2.0);
      for (let i = 0; i < slice.count; i++) {
        const gi = shipIdx + i;
        const r = total <= 1 ? 0 : Math.sqrt(gi / total) * rMax;
        const angle = gi * GOLDEN_ANGLE;
        const localFwd = Math.cos(angle) * r, localSide = Math.sin(angle) * r;
        const ph = gi * GOLDEN_ANGLE * 2.39;
        const spd = (0.4 + (gi % 5) * 0.08) * (0.3 + damp * 0.7);
        const amp = wAmp * damp;
        const wFwd = Math.sin(time * spd + ph) * amp, wSide = Math.cos(time * (spd * 1.3) + ph * 1.7) * amp;
        const wVert = Math.sin(time * (spd * 0.6) + ph * 0.7) * amp * 0.4;
        const fwd = localFwd + wFwd, side = localSide + wSide + Math.max(-0.15, Math.min(0.15, turnRate * localFwd * 0.5));
        this._px[gi] = cx + dirX * fwd + perpX * side;
        this._pz[gi] = cz + dirZ * fwd + perpZ * side;
        this._py[gi] = cy + wVert + Math.sin(gi * GOLDEN_ANGLE * 1.618) * r * 0.3 * damp;
        this._fp[gi] = slice.def.footprint;
        this._ti[gi] = ti;
        this._li[gi] = i;
      }
      shipIdx += slice.count;
    }
  }

  /** Push overlapping ships apart. */
  private _separate(n: number, cx: number, cz: number): void {
    const px = this._px, pz = this._pz, fp = this._fp;
    for (let iter = 0; iter < 2; iter++) {
      for (let a = 0; a < n; a++) {
        for (let b = a + 1; b < n; b++) {
          const dx = px[b] - px[a], dz = pz[b] - pz[a];
          const d2 = dx * dx + dz * dz;
          const minD = fp[a] + fp[b];
          if (d2 >= minD * minD || d2 < 1e-10) continue;
          const d = Math.sqrt(d2);
          const push = (minD - d) * 0.5;
          const nx = dx / d, nz = dz / d;
          px[a] -= nx * push; pz[a] -= nz * push;
          px[b] += nx * push; pz[b] += nz * push;
        }
      }
    }
    const r2 = this._maxRadius * this._maxRadius;
    for (let i = 0; i < n; i++) {
      const rx = px[i] - cx, rz = pz[i] - cz;
      const d2 = rx * rx + rz * rz;
      if (d2 <= r2) continue;
      const s = this._maxRadius / Math.sqrt(d2);
      px[i] = cx + rx * s; pz[i] = cz + rz * s;
    }
  }

  private _computeLineup(comp: readonly number[], cx: number, cy: number, cz: number): void {
    const gap = this._spacing * 5, halfW = (SHIP_TYPE_COUNT - 1) * gap * 0.5;
    for (let i = 0; i < SHIP_TYPE_COUNT; i++) {
      const s = this._slices[i], a = comp[i] ?? 0;
      if (a === 0) { s.count = 0; s.capped = false; continue; }
      s.count = 1; s.capped = false;
      writeShipMatrix(s.data, 0, cx + i * gap - halfW, cy, cz, 0, -1, s.def, 0);
    }
  }
}
